import React, { useEffect, useState } from 'react';
import Layout from '../components/shared/Layout';
import { employeeAPI, authAPI } from '../services/api';
import toast from 'react-hot-toast';
import { User, Briefcase, Lock, Save } from 'lucide-react';

export default function ProfilePage() {
  const [profile, setProfile] = useState(null);
  const [form, setForm]       = useState({});
  const [pwForm, setPwForm]   = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    employeeAPI.getMyProfile().then(res => {
      setProfile(res.data);
      setForm({
        first_name:     res.data.first_name,
        last_name:      res.data.last_name,
        birth_date:     res.data.birth_date?.split('T')[0] || '',
        phone_whatsapp: res.data.phone_whatsapp || '',
      });
    });
  }, []);

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await employeeAPI.updateMyProfile(form);
      toast.success('Perfil actualizado');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al actualizar el perfil');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (pwForm.newPassword !== pwForm.confirm) return toast.error('Las contraseñas no coinciden');
    if (pwForm.newPassword.length < 6) return toast.error('Mínimo 6 caracteres');
    setLoading(true);
    try {
      await authAPI.changePassword({ currentPassword: pwForm.currentPassword, newPassword: pwForm.newPassword });
      toast.success('Contraseña actualizada');
      setPwForm({ currentPassword: '', newPassword: '', confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al cambiar contraseña');
    } finally {
      setLoading(false);
    }
  };

  if (!profile) return (
    <Layout title="Mi perfil">
      <div style={{ padding: 32, color: 'var(--gray-400)' }}>Cargando…</div>
    </Layout>
  );

  const SectionTitle = ({ icon: Icon, children }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
      <Icon size={17} color="var(--primary)" strokeWidth={1.8} />
      <span style={{ fontWeight: 600, fontSize: 15, color: 'var(--gray-900)' }}>{children}</span>
    </div>
  );

  const Row = ({ label, value }) => (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '9px 0', borderBottom: '1px solid var(--gray-100)',
      gap: 12,
    }}>
      <span style={{ fontSize: 13, color: 'var(--gray-500)', flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 500, textAlign: 'right', wordBreak: 'break-all' }}>{value}</span>
    </div>
  );

  return (
    <Layout title="Mi perfil">
      <div className="page-header">
        <div>
          <div className="page-title">Mi perfil</div>
          <div className="page-subtitle">Gestiona tu información personal</div>
        </div>
      </div>

      <div className="profile-grid">

        {/* ── Datos personales ─────────────────────────────────────────────── */}
        <div className="card">
          <SectionTitle icon={User}>Datos personales</SectionTitle>
          <form onSubmit={handleProfileSave}>
            <div className="form-group">
              <label className="form-label">Nombre</label>
              <input className="form-input" value={form.first_name || ''}
                onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Apellido</label>
              <input className="form-input" value={form.last_name || ''}
                onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Fecha de nacimiento</label>
              <input type="date" className="form-input" value={form.birth_date || ''}
                onChange={e => setForm(f => ({ ...f, birth_date: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">WhatsApp</label>
              <input className="form-input" placeholder="+595 981 000000"
                value={form.phone_whatsapp || ''}
                onChange={e => setForm(f => ({ ...f, phone_whatsapp: e.target.value }))} />
              <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 4 }}>
                Usado para notificaciones automáticas
              </div>
            </div>
            <button type="submit" className="btn btn-primary" disabled={loading}
              style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', justifyContent: 'center' }}>
              <Save size={15} /> {loading ? 'Guardando…' : 'Guardar cambios'}
            </button>
          </form>
        </div>

        {/* ── Columna derecha ───────────────────────────────────────────────── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Info laboral */}
          <div className="card">
            <SectionTitle icon={Briefcase}>Información laboral</SectionTitle>
            <Row label="Email"        value={profile.email} />
            <Row label="Departamento" value={profile.department || '—'} />
            <Row label="Cargo"        value={profile.position || '—'} />
            <Row label="Días totales" value={profile.vacation_days_total} />
            <Row label="Días usados"  value={profile.vacation_days_used} />
            <Row label="Disponibles"  value={profile.vacation_days_total - profile.vacation_days_used} />
          </div>

          {/* Cambiar contraseña */}
          <div className="card">
            <SectionTitle icon={Lock}>Cambiar contraseña</SectionTitle>
            <form onSubmit={handlePasswordChange}>
              <div className="form-group">
                <label className="form-label">Contraseña actual</label>
                <input type="password" className="form-input" value={pwForm.currentPassword}
                  onChange={e => setPwForm(f => ({ ...f, currentPassword: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label className="form-label">Nueva contraseña</label>
                <input type="password" className="form-input" value={pwForm.newPassword}
                  onChange={e => setPwForm(f => ({ ...f, newPassword: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label className="form-label">Confirmar nueva contraseña</label>
                <input type="password" className="form-input" value={pwForm.confirm}
                  onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))} required />
              </div>
              <button type="submit" className="btn btn-secondary" disabled={loading}
                style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', justifyContent: 'center' }}>
                <Lock size={14} /> {loading ? 'Actualizando…' : 'Cambiar contraseña'}
              </button>
            </form>
          </div>

        </div>
      </div>
    </Layout>
  );
}
